// src/components/EntityDetailPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useOutletContext, Link, useNavigate } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
// Import your specific entity queries (you'll need to create/adjust these)
import { entityDetailQueries, updateQueries } from '../repository/graphQueries'; // Assuming a new query section
import { formatDate, formatDateTime } from '../utils';
import CallEvalDisplay from './CallEvalDisplay'; // Adjust path if needed
import {
    GOAL_STATUS_OPTIONS,
    INITIATIVE_STATUS_OPTIONS,
    ACTION_ITEM_STATUS_OPTIONS,
    ISSUE_STATUS_OPTIONS,
    ISSUE_SEVERITY_OPTIONS,
    GENERIC_STATUS_OPTIONS
} from '../constants';

// --- Helper Components ---
const TrendChart = ({ data, dataKey, title, yAxisLabel, height = 200 }) => {
    if (!data || data.length === 0) return <div className="p-4 text-sm text-gray-500">No {title} data available.</div>;

    // --- Process data to ensure 'date' key exists and is formatted ---
    const formattedData = data.map(item => {
        // Identify the date key (wk from sentiment, weekBeginning from mentions)
        const dateValue = item.wk || item.weekBeginning;
        return {
            ...item,
            // Create a 'date' key with the formatted string for the XAxis
            date: dateValue ? formatDate(dateValue) : 'Unknown Date' // Use formatDate utility
        };
    }).filter(item => item.date !== 'Unknown Date'); // Filter out items where date couldn't be determined

    // Sort data by date just in case it's not already sorted
    formattedData.sort((a, b) => new Date(a.date) - new Date(b.date));

    return (
        <div className="bg-white rounded-lg shadow p-4 mt-4">
            <h3 className="text-md font-semibold mb-3">{title}</h3>
            <ResponsiveContainer width="100%" height={height}>
                <LineChart data={formattedData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} label={{ value: yAxisLabel, angle: -90, position: 'insideLeft', fontSize: 10, offset: -5 }}/>
                    <Tooltip wrapperClassName="text-xs" />
                    <Line type="monotone" dataKey={dataKey} stroke="#3B82F6" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
};

const LinkedItemsList = ({ items, title, linkPrefix, isLoading, error }) => {
    if (isLoading) return <div className="p-2 text-xs text-gray-500">Loading {title}...</div>;
    if (error) return <div className="p-2 text-xs text-red-500">Error: {error}</div>;
    if (!items || items.length === 0) return <div className="p-2 text-xs text-gray-400">No {title}.</div>;

    return (
        <div className="mt-4">
            <h3 className="text-md font-semibold mb-2">{title}</h3>
            <ul className="list-disc pl-5 space-y-1 text-sm">
                {items.map(item => (
                    <li key={item.id}>
                        <Link to={`${linkPrefix}/${item.id}`} className="text-blue-600 hover:underline">
                            {item.name}
                        </Link>
                        {/* Add other relevant details like status, severity etc. */}
                        {item.status && <span className="ml-2 text-xs text-gray-500">({item.status})</span>}
                        {item.severity && <span className="ml-2 text-xs text-gray-500">({item.severity})</span>}
                        {/* Display snippet for communications */}
                        {item.snippet && <blockquote className="mt-1 pl-2 border-l-2 border-gray-300 italic text-gray-500 text-xs">"{item.snippet}"</blockquote>}
                    </li>
                ))}
            </ul>
        </div>
    );
};


const EntityDetailPage = ({ entityType: entityTypeFromProp }) => { // MODIFIED: Accept entityType as a prop
    const { entityId } = useParams(); // entityId from params (department context not required)
    const navigate = useNavigate();
    const { neo4jService } = useOutletContext() || {};

    const [entityData, setEntityData] = useState(null);
    const [mentionTrend, setMentionTrend] = useState([]);
    const [sentimentTrend, setSentimentTrend] = useState([]);
    const [linkedComms, setLinkedComms] = useState([]);
    const [linkedIssues, setLinkedIssues] = useState([]);
    const [linkedInitiatives, setLinkedInitiatives] = useState([]);

    const [loading, setLoading] = useState(true); // Initial state
    const [error, setError] = useState(null);

    const [loadingComms, setLoadingComms] = useState(true);
    const [loadingIssues, setLoadingIssues] = useState(true);
    const [loadingInitiatives, setLoadingInitiatives] = useState(true);

    const [isUpdating, setIsUpdating] = useState(false);
    const [updateError, setUpdateError] = useState(null);

    const fetchData = useCallback(async () => {
        // MODIFIED: Use entityTypeFromProp
        if (!neo4jService || !entityTypeFromProp || !entityId) {
            setError("Missing necessary parameters to fetch data.");
            setLoading(false); // Ensure loading is stopped
            setLoadingComms(false);
            setLoadingIssues(false);
            setLoadingInitiatives(false);
            return;
        }

        // setLoading(true); // Set by useEffect calling this
        setError(null);
        // Ensure sub-loading states are reset at the beginning of a fetch attempt
        setLoadingComms(true);
        setLoadingIssues(true);
        setLoadingInitiatives(true);

        console.log(`Fetching details for ${entityTypeFromProp} ${entityId}`);

        try {
            let detailQueryKey, mentionQueryKey, sentimentQueryKey, commsQueryKey, issuesQueryKey, initiativesQueryKey;

            // MODIFIED: Use entityTypeFromProp in switch
            switch (entityTypeFromProp) {
                case 'goals':
                    detailQueryKey = 'getGoalDetails';
                    mentionQueryKey = 'getGoalMentionTrend';
                    sentimentQueryKey = 'getGoalSentimentTrend';
                    commsQueryKey = 'getGoalLinkedComms';
                    issuesQueryKey = 'getGoalBlockingIssues';
                    initiativesQueryKey = 'getGoalSupportingInitiatives';
                    break;
                case 'initiatives':
                    detailQueryKey = 'getInitiativeDetails';
                    mentionQueryKey = 'getInitiativeMentionTrend';
                    sentimentQueryKey = 'getInitiativeSentimentTrend';
                    commsQueryKey = 'getInitiativeLinkedComms';
                    issuesQueryKey = 'getInitiativeBlockingIssues';
                    break;
                case 'issues':
                    detailQueryKey = 'getIssueDetails';
                    mentionQueryKey = 'getIssueMentionTrend';
                    sentimentQueryKey = 'getIssueSentimentTrend';
                    commsQueryKey = 'getIssueLinkedComms';
                    initiativesQueryKey = 'getIssueBlockedInitiatives';
                    break;
                case 'action-items':
                    detailQueryKey = 'getActionItemDetails';
                    mentionQueryKey = 'getActionItemMentionTrend';
                    sentimentQueryKey = 'getActionItemSentimentTrend';
                    commsQueryKey = 'getActionItemLinkedComms';
                    initiativesQueryKey = 'getActionItemSupportedInitiative';
                    break;
                default:
                    throw new Error(`Unknown entity type: ${entityTypeFromProp}`);
            }

            const promises = [];
            promises.push(entityDetailQueries[detailQueryKey] ? neo4jService.runQuery(entityDetailQueries[detailQueryKey], { entityId }) : Promise.resolve(null));
            promises.push(entityDetailQueries[mentionQueryKey] ? neo4jService.runQuery(entityDetailQueries[mentionQueryKey], { entityId }) : Promise.resolve([]));
            promises.push(entityDetailQueries[sentimentQueryKey] ? neo4jService.runQuery(entityDetailQueries[sentimentQueryKey], { entityId }) : Promise.resolve([]));

            // Ensure finally blocks for sub-loaders are separate if queries can fail independently
            promises.push(entityDetailQueries[commsQueryKey] ? neo4jService.runQuery(entityDetailQueries[commsQueryKey], { entityId }).finally(()=> setLoadingComms(false)) : Promise.resolve([]).finally(()=> setLoadingComms(false)));
            promises.push(entityDetailQueries[issuesQueryKey] ? neo4jService.runQuery(entityDetailQueries[issuesQueryKey], { entityId }).finally(()=> setLoadingIssues(false)) : Promise.resolve([]).finally(()=> setLoadingIssues(false)));
            promises.push(entityDetailQueries[initiativesQueryKey] ? neo4jService.runQuery(entityDetailQueries[initiativesQueryKey], { entityId }).finally(()=> setLoadingInitiatives(false)) : Promise.resolve([]).finally(()=> setLoadingInitiatives(false)));

            const [detailsResult, mentionResult, sentimentResult, commsResult, issuesResult, initiativesResult] = await Promise.all(promises);

            setEntityData(detailsResult && detailsResult.length > 0 ? detailsResult[0] : null);
            setMentionTrend(mentionResult || []);
            setSentimentTrend(sentimentResult || []);
            setLinkedComms(commsResult || []);
            setLinkedIssues(issuesResult || []);
            setLinkedInitiatives(initiativesResult || []);

        } catch (err) {
            console.error(`Error fetching data for ${entityTypeFromProp} ${entityId}:`, err);
            setError(`Failed to load details: ${err.message}`);
            setEntityData(null); setMentionTrend([]); setSentimentTrend([]);
            setLinkedComms([]); setLinkedIssues([]); setLinkedInitiatives([]);
        } finally {
            setLoading(false); // Set main loading false
            // Sub-loading states are handled in their respective promise finally blocks
        }
    }, [neo4jService, entityTypeFromProp, entityId]); // MODIFIED: Use entityTypeFromProp only

    useEffect(() => {
        setLoading(true); // Set loading to true before each fetch
        fetchData();
    }, [fetchData]);

    const handleFieldChange = async (fieldName, newValue) => {
        if (!entityData || !neo4jService || isUpdating) return;

        console.log(`Updating ${fieldName} for ${entityTypeFromProp} ${entityId} to ${newValue}`); // MODIFIED
        setIsUpdating(true);
        setUpdateError(null);
        const previousValue = entityData[fieldName];
        setEntityData(prev => ({ ...prev, [fieldName]: newValue }));

        try {
            let query;
            if (fieldName === 'status') {
                query = updateQueries.updateNodeStatus;
            } else if (fieldName === 'severity' && entityTypeFromProp === 'issues') { // MODIFIED
                query = updateQueries.updateNodeSeverity;
            } else {
                throw new Error(`Updating property "${fieldName}" is not supported for ${entityTypeFromProp}`); // MODIFIED
            }
            await neo4jService.runQuery(query, {
                nodeId: entityId,
                newValue: newValue
            });
            console.log(`${fieldName} updated successfully!`);
        } catch (err) {
            console.error(`Failed to update ${fieldName}:`, err);
            setUpdateError(`Failed to update ${fieldName}. Please try again.`);
            setEntityData(prev => ({ ...prev, [fieldName]: previousValue }));
        } finally {
            setIsUpdating(false);
        }
    };

    const getStatusOptions = () => {
        // MODIFIED: Use entityTypeFromProp
        switch(entityTypeFromProp) {
            case 'goals': return GOAL_STATUS_OPTIONS;
            case 'initiatives': return INITIATIVE_STATUS_OPTIONS;
            case 'action-items': return ACTION_ITEM_STATUS_OPTIONS;
            case 'issues': return ISSUE_STATUS_OPTIONS;
            default:
                console.warn(`No specific status options for entity type: ${entityTypeFromProp}. Using generic list.`);
                return GENERIC_STATUS_OPTIONS;
        }
    };

    // --- Current options derived from the prop ---
    const currentStatusOptions = getStatusOptions();
    const currentSeverityOptions = entityTypeFromProp === 'issues' ? ISSUE_SEVERITY_OPTIONS : []; // MODIFIED

    // --- Render Logic ---
    if (loading) {
        return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-700"></div><span className="ml-2 text-gray-700">Loading Details...</span></div>;
    }

    if (error) {
        return <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded"><p><strong>Error:</strong> {error}</p></div>;
    }

    if (!entityData) {
         return <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 rounded"><p><strong>Not Found:</strong> Could not load data for {entityTypeFromProp} with ID {entityId}.</p></div>;
    }

    // Determine link prefixes for internal routes (department-agnostic)
    const basePath = `/internal`;
    const issueLinkPrefix = `${basePath}/issues`;
    const initiativeLinkPrefix = `${basePath}/initiatives`;


    // Ensure the link back uses entityTypeFromProp
    const entityTypeDisplay = entityTypeFromProp ? entityTypeFromProp.charAt(0).toUpperCase() + entityTypeFromProp.slice(1) : 'List';

    return (
        <div>
            {/* <Link to={`/clients/${clientId}/${entityTypeFromProp}`} className="text-sm text-blue-600 hover:underline mb-4 inline-block">
                &larr; Back to {entityTypeDisplay}
            </Link> */}
            <button onClick={() => navigate(-1)} className="text-sm text-blue-600 hover:underline mb-4 inline-block">
                &larr; Back
            </button>

            {isUpdating && <div className="mb-4 p-2 text-sm bg-blue-100 text-blue-700 rounded">Updating...</div>}
            {updateError && <div className="mb-4 p-2 text-sm bg-red-100 text-red-700 rounded">Error: {updateError}</div>}

            {entityData && (
                <div className="bg-white rounded-lg shadow p-6 mb-6">
                    <h1 className="text-2xl font-bold text-gray-800 mb-2">{entityData.name || 'No Name'}</h1>
                    <p className="text-gray-600 mb-3">{entityData.description || <span className="italic">No description provided.</span>}</p>

                    <div className="flex flex-wrap gap-x-6 gap-y-3 text-sm items-center">
                         {entityData.hasOwnProperty('status') && (
                             <div className="flex items-center space-x-2">
                                 <strong className="text-gray-700">Status:</strong>
                                 <select
                                     value={entityData.status || ''}
                                     onChange={(e) => handleFieldChange('status', e.target.value)}
                                     disabled={isUpdating}
                                     className={`p-1 border rounded text-sm ${isUpdating ? 'bg-gray-100 cursor-not-allowed' : 'border-gray-300 bg-white hover:border-gray-400'}`}
                                 >
                                     {entityData.status && !currentStatusOptions.includes(entityData.status) && (
                                        <option value={entityData.status} disabled>{String(entityData.status).charAt(0).toUpperCase() + String(entityData.status).slice(1)} (Current)</option>
                                     )}
                                     {currentStatusOptions.map(option => (
                                         <option key={option} value={option}>
                                             {option.charAt(0).toUpperCase() + option.slice(1)}
                                         </option>
                                     ))}
                                 </select>
                             </div>
                         )}
                         {entityTypeFromProp === 'issues' && entityData.hasOwnProperty('severity') && ( // MODIFIED
                              <div className="flex items-center space-x-2">
                                 <strong className="text-gray-700">Severity:</strong>
                                 <select
                                     value={entityData.severity || ''}
                                     onChange={(e) => handleFieldChange('severity', e.target.value)}
                                     disabled={isUpdating}
                                     className={`p-1 border rounded text-sm ${isUpdating ? 'bg-gray-100 cursor-not-allowed' : 'border-gray-300 bg-white hover:border-gray-400'}`}
                                 >
                                     {entityData.severity && !currentSeverityOptions.includes(entityData.severity) && (
                                        <option value={entityData.severity} disabled>{entityData.severity.charAt(0).toUpperCase() + entityData.severity.slice(1)} (Current)</option>
                                     )}
                                     {currentSeverityOptions.map(option => (
                                         <option key={option} value={option}>
                                             {option.charAt(0).toUpperCase() + option.slice(1)}
                                         </option>
                                     ))}
                                 </select>
                              </div>
                         )}
                         {entityData.deadline && <span><strong>Deadline:</strong> {formatDate(entityData.deadline)}</span>}
                         {entityData.assignee && <span><strong>Assignee:</strong> {entityData.assignee}</span>}
                         {entityData.targetDate && <span><strong>Target Date:</strong> {formatDate(entityData.targetDate)}</span>}
                    </div>
                </div>
            )}

            {/* Trends Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <TrendChart data={mentionTrend} dataKey="mentions" title="Mention Trend (Weekly)" yAxisLabel="Mentions" />
                <TrendChart data={sentimentTrend} dataKey="avgSentiment" title="Sentiment Trend (Weekly)" yAxisLabel="Avg. Sentiment" />
            </div>

            {/* Linked Items Section */}
            <div className="bg-white rounded-lg shadow p-6">
                 <h2 className="text-xl font-semibold mb-4">Linked Items</h2>

                 {/* Render linked items based on entity type */}
                 {/* Example for a Goal */}
                 {entityTypeFromProp === 'goals' && (
                     <>
                        <LinkedItemsList items={linkedInitiatives} title="Supporting Initiatives" linkPrefix={initiativeLinkPrefix} isLoading={loadingInitiatives} />
                        <LinkedItemsList items={linkedIssues} title="Blocking Issues (via Initiatives)" linkPrefix={issueLinkPrefix} isLoading={loadingIssues} />
                     </>
                 )}

                 {/* Example for an Initiative */}
                  {entityTypeFromProp === 'initiatives' && (
                     <>
                         {/* Need query for supported goals if not in details */}
                         {/* <LinkedItemsList items={linkedGoals} title="Supported Goals" linkPrefix={goalLinkPrefix} /> */}
                         <LinkedItemsList items={linkedIssues} title="Blocking Issues" linkPrefix={issueLinkPrefix} isLoading={loadingIssues} />
                         {/* Need query for Action Items supporting this initiative */}
                         {/* <LinkedItemsList items={linkedActionItems} title="Action Items" linkPrefix={`/clients/${clientId}/action-items`} /> */}
                     </>
                 )}

                  {/* Example for an Issue */}
                  {entityTypeFromProp === 'issues' && (
                     <>
                         <LinkedItemsList items={linkedInitiatives} title="Blocked Initiatives" linkPrefix={initiativeLinkPrefix} isLoading={loadingInitiatives} />
                     </>
                 )}

                 {/* Example for an Action Item */}
                 {entityTypeFromProp === 'action-items' && (
                      <>
                          <LinkedItemsList items={linkedInitiatives} title="Supporting Initiative" linkPrefix={initiativeLinkPrefix} isLoading={loadingInitiatives} />
                      </>
                  )}

            </div>
            {/* Column 2: Related Communications */}
            <div>
                <h3 className="text-md font-semibold mb-2">Related Communications</h3>
                {loadingComms && <div className="p-2 text-xs text-gray-500">Loading communications...</div>}
                {!loadingComms && linkedComms.length === 0 && <div className="p-2 text-xs text-gray-400">No related communications found.</div>}
                {!loadingComms && linkedComms.length > 0 && (
                    <div className="space-y-4">
                        {linkedComms.map(comm => (
                            <div key={comm.id} className="border rounded-md p-3 bg-gray-50 shadow-sm">
                                <div className="flex justify-between items-center mb-1">
                                    <span className="text-sm font-medium text-gray-800">{comm.title || `(${comm.type || 'Communication'})`}</span>
                                    <span className="text-xs text-gray-500">{formatDateTime(comm.date)}</span>
                                </div>
                                <div className="text-xs text-gray-600 mb-2">
                                    {comm.staff && <span>Staff: {comm.staff}</span>}
                                    {comm.type && <span className="ml-2 italic">({comm.type})</span>}
                                </div>
                                {comm.summary && (
                                    <p className="text-sm text-gray-700 mb-2 whitespace-pre-line">{comm.summary}</p>
                                )}
                                {/* --- Use CallEvalDisplay component --- */}
                                <div className="mt-2 pt-2 border-t border-gray-200">
                                    <CallEvalDisplay evaluations={comm.callEvaluations} />
                                </div>
                                {/* --- End Call Evaluations Display --- */}
                                {/* Display Snippets */}
                                {Array.isArray(comm.snippets) && comm.snippets.length > 0 && (
                                    <div>
                                        <h4 className="text-xs font-semibold text-gray-600 mb-1">Relevant Snippets:</h4>
                                        <div className="space-y-1">
                                            {comm.snippets.map((snippet, index) => (
                                                    snippet && ( // Render only if snippet is not null/empty
                                                    <blockquote key={index} className="pl-2 border-l-2 border-blue-300 italic text-gray-600 text-xs bg-blue-50 p-1 rounded">
                                                        "{snippet}"
                                                    </blockquote>
                                                    )
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div> {/* End Communications Column */}

        </div>
    );
};

export default EntityDetailPage;